import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { serialize } from '../utils/kql';
import { parseForBuilder, readSearchTerm, withSearchTerm } from '../utils/kqlBuilder';
import { SEARCH_MAX_CHARS, searchRefusalSentence } from '../utils/searchRefusal';

/** How long typing rests before the term is applied (KPR-740). */
export const SEARCH_DEBOUNCE_MS = 300;
/** The shortest term that is applied, in characters (code points, so an emoji is one). */
export const SEARCH_MIN_CHARS = 2;

const lengthOf = text => Array.from(text).length;

/**
 * What a box's text would apply: the trimmed term, or '' when it is too short
 * to apply (or too long to send). The URL and the box agree when this equals
 * the term in `q`; only then is nothing left to sync.
 */
export function intendedTerm(text, { minChars = SEARCH_MIN_CHARS, maxChars = SEARCH_MAX_CHARS } = {}) {
	const trimmed = `${text || ''}`.trim();
	const size = lengthOf(trimmed);
	return size >= minChars && size <= maxChars ? trimmed : '';
}

/**
 * The search box of a list of items (KPR-740, docs/platform-standards.md
 * section 5, "Every list filters the same way"): sugar over the ONE top-level
 * `text ~ "term"` condition of the list's own query text.
 *
 *   const search = useListSearch({ query, setQuery });
 *   <ListSearchBox search={search} id='items-search' />
 *
 * The query text is the source of truth - the collection list keeps it in the
 * address, a related list in the section's state - and the box is a scratch
 * copy of its term. Everything else in `q` is the chips' and the builder's and
 * is never touched (`withSearchTerm` keeps it).
 *
 * - **Applies from 2 characters**, 300 ms after the typing rests. A term under
 *   two characters applies nothing (and takes a term already applied off the
 *   list). Clearing the box clears the term at once. Enter applies at once.
 * - **A term over the server's 200 characters** is not sent (a URL of
 *   thousands of characters would break the whole filter): the box says so, in
 *   the server's own words, and the list keeps what it had.
 * - **Composition.** An IME is mid-word between `compositionstart` and
 *   `compositionend`: nothing is applied until it ends.
 * - **The address wins, but never mid-keystroke.** A chip dismissed elsewhere,
 *   a saved view applied, Back: the box takes the term in `q`. It does not
 *   while a keystroke is waiting to apply, and it does not rewrite text that
 *   would apply as that same term (a trailing space, a single character).
 * - **A refused term** (`onRefused`, handed to `useListFilter` as
 *   `onSearchRefused`): the server said no to the search itself (too many
 *   words, too broad, ...). The list keeps its other filters and goes back to
 *   the last term that was accepted, or to none; the box keeps what the person
 *   typed, with the server's sentence beside it, until they edit it. Every
 *   refused term is remembered, so the way back can never name a term that was
 *   itself refused (no loop).
 * - A late answer for an older term never replaces a newer one: the feed
 *   (useCollectionItems) drops any response that is not for its current
 *   question.
 *
 * Returns the box's pieces: `text`, `applied` (the term in `q`), `notice`
 * (`{ message }` or null), the input handlers, `clear()` and `onRefused`.
 */
export default function useListSearch({
	query,
	setQuery,
	debounceMs = SEARCH_DEBOUNCE_MS,
	minChars = SEARCH_MIN_CHARS,
	maxChars = SEARCH_MAX_CHARS,
}) {
	const applied = useMemo(() => readSearchTerm(parseForBuilder(query).ast, 'text'), [query]);
	// What the rest of the query is, for "Clear filters" beside "Clear search":
	// whether anything but the term narrows the list, and the query that is the
	// term alone (undefined for none).
	const { otherFilters, termOnlyQuery } = useMemo(() => {
		const { ast } = parseForBuilder(query);
		const rest = withSearchTerm(ast, '', 'text');
		const only = applied ? withSearchTerm(null, applied, 'text') : null;
		return { otherFilters: Boolean(rest), termOnlyQuery: only ? serialize(only) : undefined };
	}, [query, applied]);
	const [text, setTextState] = useState(applied);
	const [notice, setNoticeState] = useState(null);

	const textRef = useRef(applied);
	const noticeRef = useRef(null);
	const timerRef = useRef(null);
	const composingRef = useRef(false);
	// Read when a timer or an answer fires, long after this render's closure.
	const queryRef = useRef(query);
	queryRef.current = query;
	const appliedRef = useRef(applied);
	appliedRef.current = applied;
	const setQueryRef = useRef(setQuery);
	setQueryRef.current = setQuery;
	// The terms this box put in `q` (the address's own included), newest last,
	// and the ones the server refused.
	const acceptedRef = useRef(applied ? [applied] : []);
	const refusedRef = useRef(new Set());

	const setText = useCallback((value) => {
		textRef.current = value;
		setTextState(value);
	}, []);
	const setNotice = useCallback((value) => {
		noticeRef.current = value;
		setNoticeState(value);
	}, []);

	const cancelTimer = useCallback(() => {
		clearTimeout(timerRef.current);
		timerRef.current = null;
	}, []);
	useEffect(() => cancelTimer, [cancelTimer]);

	// Put `term` ('' for none) into `q`, leaving the rest of the query alone.
	const write = useCallback((term) => {
		const { ast } = parseForBuilder(queryRef.current);
		const next = withSearchTerm(ast, term, 'text');
		setQueryRef.current(next ? serialize(next) : undefined);
	}, []);

	const commit = useCallback((term) => {
		cancelTimer();
		if (term === appliedRef.current) return;
		if (term) acceptedRef.current.push(term);
		write(term);
	}, [cancelTimer, write]);

	// What the box holds, applied now.
	const settle = useCallback((value) => {
		cancelTimer();
		// A new attempt supersedes what was said about the last one.
		if (noticeRef.current) setNotice(null);
		const trimmed = `${value || ''}`.trim();
		const size = lengthOf(trimmed);
		if (size > maxChars) {
			// Not sent: the list keeps what it had, and the person is told.
			setNotice({ message: `A search is at most ${maxChars} characters; this one is ${size}.` });
			return;
		}
		commit(size >= minChars ? trimmed : '');
	}, [cancelTimer, commit, maxChars, minChars, setNotice]);

	const schedule = useCallback((value) => {
		cancelTimer();
		if (!`${value || ''}`.trim()) {
			// Emptied (the box's clear, or backspacing to nothing) does not wait.
			commit('');
			return;
		}
		if (composingRef.current) return;
		timerRef.current = setTimeout(() => {
			timerRef.current = null;
			settle(textRef.current);
		}, debounceMs);
	}, [cancelTimer, commit, debounceMs, settle]);

	const onChange = useCallback((event) => {
		const value = event?.target?.value ?? '';
		setText(value);
		if (noticeRef.current) setNotice(null);
		schedule(value);
	}, [schedule, setNotice, setText]);

	const onCompositionStart = useCallback(() => {
		composingRef.current = true;
		cancelTimer();
	}, [cancelTimer]);
	const onCompositionEnd = useCallback((event) => {
		composingRef.current = false;
		const value = event?.target?.value ?? textRef.current;
		setText(value);
		schedule(value);
	}, [schedule, setText]);

	const onPressEnter = useCallback((event) => {
		if (composingRef.current || event?.nativeEvent?.isComposing) return;
		settle(textRef.current);
	}, [settle]);

	const clear = useCallback(() => {
		setText('');
		setNotice(null);
		commit('');
	}, [commit, setNotice, setText]);

	// The address -> the box. Not while a keystroke waits (the box wins), not
	// while composing, and not for text that would apply as the term already
	// in `q`. A term the server refused keeps its text and its sentence until
	// the person edits the box or `q` moves on without us.
	useEffect(() => {
		if (timerRef.current || composingRef.current) return;
		const current = noticeRef.current;
		if (current && current.revertedTo !== undefined && current.revertedTo === applied) return;
		if (current) setNotice(null);
		if (intendedTerm(textRef.current, { minChars, maxChars }) === applied) return;
		setText(applied);
	}, [applied, minChars, maxChars, setNotice, setText]);

	// The server refused the term in `q`. Returns whether it was the box's.
	const onRefused = useCallback((body, serverMessage) => {
		const refused = readSearchTerm(parseForBuilder(queryRef.current).ast, 'text');
		// A search the box did not write (a worklist's stored filter, say) is the
		// list's to answer, not ours.
		if (!refused) return false;
		refusedRef.current.add(refused);
		let revertTo = '';
		for (let index = acceptedRef.current.length - 1; index >= 0; index -= 1) {
			const candidate = acceptedRef.current[index];
			if (candidate !== refused && !refusedRef.current.has(candidate)) {
				revertTo = candidate;
				break;
			}
		}
		setNotice({ message: searchRefusalSentence(body, serverMessage), revertedTo: revertTo });
		// The box keeps what the person typed; the list goes back to what it had.
		if (textRef.current.trim() === '') setText(refused);
		write(revertTo);
		return true;
	}, [setNotice, setText, write]);

	return {
		text,
		applied,
		otherFilters,
		termOnlyQuery,
		notice,
		onChange,
		onCompositionStart,
		onCompositionEnd,
		onPressEnter,
		clear,
		onRefused,
	};
}
